import React, { ReactNode } from 'react';

import { Button, Breadcrumb, Input, Radio, Typography, Select } from 'antd';
import { RadioChangeEvent } from 'antd/es/radio';
import { ButtonType } from 'antd/es/button';
import { ItemType } from 'antd/es/breadcrumb/Breadcrumb';
import { usePersistedUser } from '../../../hooks/usePersistedUser';
import BuildingIndicator from '../../BuildingIndicator';

import { Link } from 'react-router-dom';

const { Search } = Input;

const { Text } = Typography;

interface IProps {
	/**
	 * Omit when the surrounding frame already names the screen. The Shop Floor
	 * App renders these pages inside a named tab, so a repeated heading costs
	 * vertical space on a 10.1" tablet without telling the operator anything.
	 */
	title?: string;
	link?: string;
	showLinkButton?: boolean;
	showOnClickButton?: boolean;
	buttonText?: string;
	icon?: React.ReactNode;
	type?: ButtonType;
	handleOnClick?: () => void;
	breadcrumbItems?: ItemType[];
	onRadioChange?: (e: RadioChangeEvent) => void;
	onSearch?: (value: string) => void;
	onDefaultUserChange?: (value: string) => void;
	radioOptions?: ReactNode;
	radioValue?: string;
	titleStyle?: React.CSSProperties;
	actionButtonStyle?: React.CSSProperties;
	searchStyle?: React.CSSProperties;
	radioGroupStyle?: React.CSSProperties;
	searchScale?: number;
	searchPlaceholder?: string;
	/**
	 * Sits beside the building badge, for anything else that says where this
	 * tablet is or what it is set up to do. It belongs next to the building
	 * because it reads the same way: something configured once for the station,
	 * not a control the operator works with.
	 */
	stationExtra?: ReactNode;
	/**
	 * A second action, rendered before the Back button. Pages that need both
	 * (Cutting carries Back plus Create a Reservation) pass it here rather than
	 * competing for the single action slot, which would otherwise force the
	 * search box and status radios to give up room.
	 */
	secondaryAction?: ReactNode;
}

const TabletHeader: React.FC<IProps> = ({
	title,
	link,
	showLinkButton,
	showOnClickButton,
	buttonText,
	icon,
	type = 'primary',
	handleOnClick,
	breadcrumbItems,
	onSearch,
	onRadioChange,
	onDefaultUserChange,
	radioOptions,
	radioValue,
	titleStyle,
	actionButtonStyle,
	searchStyle,
	radioGroupStyle,
	searchScale,
	searchPlaceholder = 'ID, Location',
	stationExtra,
	secondaryAction,
}) => {
	const queryParams = new URLSearchParams(location.search);
	const currentSearch = queryParams.get('search') || '';
	const [selectedUser, setSelectedUser, users] = usePersistedUser(
		'Machine Operators',
		'selectedUser'
	);

	return (
		<div>
			<div className="flex flex-col md:flex-row justify-between py-4">
				<div className="flex items-center gap-3">
					{title ? (
						<h1 className="text-2xl font-bold m-0" style={titleStyle}>
							{title}
						</h1>
					) : null}
					<BuildingIndicator />
					{stationExtra}
				</div>

				<div className="flex items-center flex-row gap-4">
					{onDefaultUserChange && users && (
						<div className="flex flex-row gap-4 justify-end items-center">
							<Text type="secondary">Default User:</Text>
							<Select
								value={selectedUser?.id || undefined}
								className="flex"
								onChange={(userId) => {
									const user = users.find((u) => u.id === userId);
									if (user) {
										setSelectedUser(user);
										if (onDefaultUserChange) {
											onDefaultUserChange(String(userId));
										}
									}
								}}
								variant="borderless"
								size="large"
								options={users.map((user) => ({
									value: user.id,
									label: user.full_name || user.first_name,
								}))}
								style={{ width: '150px' }}
							/>
						</div>
					)}

					{onRadioChange && (
						<Radio.Group
							value={radioValue}
							onChange={onRadioChange}
							size="large"
							optionType="button"
							buttonStyle="solid"
							style={radioGroupStyle}
						>
							{radioOptions}
						</Radio.Group>
					)}

					{onSearch && (
						<div
							style={{
								...(searchScale && searchScale !== 1
									? {
											transform: `scale(${searchScale})`,
											transformOrigin: 'left center',
									  }
									: {}),
								// Reserve visual width in layout so scaled input does not overlap siblings.
								width: `${
									(searchStyle?.width ? Number(searchStyle.width) : 250) *
									(searchScale || 1)
								}px`,
							}}
						>
							<Search
								placeholder={searchPlaceholder}
								allowClear
								onSearch={onSearch}
								defaultValue={currentSearch}
								// className="select-material-search"
								style={{ width: 250, ...searchStyle }}
								size="large"
							/>
						</div>
					)}

					{secondaryAction}

					{showLinkButton && link && (
						<Link to={link}>
							<Button icon={icon} type={type} className="mt-2 md:mt-0">
								{buttonText}
							</Button>
						</Link>
					)}

					{showOnClickButton && (
						<div>
							<Button
								icon={icon}
								type={type}
								onClick={() => handleOnClick && handleOnClick()}
								className="mt-2 md:mt-0"
								size="large"
								style={actionButtonStyle}
							>
								{buttonText}
							</Button>
						</div>
					)}
				</div>
			</div>
			{breadcrumbItems && breadcrumbItems?.length >= 1 && (
				<Breadcrumb
					style={{ marginBottom: '1.5rem', marginTop: '10px' }}
					items={breadcrumbItems}
				/>
			)}
		</div>
	);
};

export default TabletHeader;
